/**
 * Блок «Системы» в карточке техкарты (AK-773, решение founder Р35).
 *
 * Что он говорит агроному: защита, питание и обработка почвы в этой карте —
 * не разрозненные строки, а ВСТАВЛЕННЫЕ системы, у каждой своя стоимость на
 * гектар. Систему можно заменить целиком — карта пересчитается; можно убрать.
 *
 * Почему блок отдельный, а не плитки «блоков» рядом: плитки блоков — это срез
 * строк САМОЙ карты, а система живёт в библиотеке и переиспользуется другими
 * картами. Смешать их значило бы обещать, что «заменить блок» и «заменить
 * систему» — одно действие; это разные вещи и разные последствия.
 */
import { useState } from 'react';
import { Link } from '@tanstack/react-router';
import { Button } from '@/components/ui/button';
import { Badge } from '@/components/ui/badge';
import { Card, CardContent } from '@/components/ui/card';
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
} from '@/components/ui/dialog';
import { Skeleton } from '@/components/ui/skeleton';
import { toast } from '@/lib/toast';
import { formatPerHa, techCardErrorText } from '@/lib/planning/techCards';
import {
  MODULE_KIND_SHORT,
  formatTreatments,
  useCardModules,
  useRemoveCardModule,
  useReplaceCardModule,
  type CardModuleRow,
} from '@/lib/planning/techCardModules';
import { SystemPickerDialog, type PickerMode } from './SystemPickerDialog';

export function SystemsBlock({
  cardId,
  seasonId,
  cropId,
  canWrite,
}: {
  cardId: string;
  seasonId: string | null;
  cropId: string | null;
  canWrite: boolean;
}) {
  const modules = useCardModules(cardId);
  const remove = useRemoveCardModule(cardId);
  // «Обновить» = замена системы на саму себя: бэк снимает её строки и ставит
  // свежие из библиотеки одной транзакцией (AK-773, ревью head-backend).
  const refresh = useReplaceCardModule(cardId);
  const [picker, setPicker] = useState<PickerMode | null>(null);
  const [removeFor, setRemoveFor] = useState<CardModuleRow | null>(null);

  const items = modules.data ?? [];

  return (
    <Card>
      <CardContent className="p-4">
        <div className="flex flex-wrap items-center gap-2">
          <h3 className="text-base font-semibold text-foreground">Системы в этой карте</h3>
          {/* Вход в библиотеку — отсюда: собирают систему там, а пользуются
              здесь, и искать раздел в меню человеку не надо. */}
          <Button asChild variant="link" size="sm" className="h-auto p-0">
            <Link to="/planning/systems">Библиотека систем</Link>
          </Button>
          {canWrite && (
            <Button size="sm" className="ml-auto" onClick={() => setPicker({ kind: 'insert' })}>
              Вставить систему
            </Button>
          )}
        </div>
        <p className="mt-1 max-w-4xl text-sm text-muted-foreground">
          Система защиты, питания и обработки почвы собирается один раз в библиотеке и
          вставляется в карту целиком. Замена системы пересчитывает себестоимость карты.
        </p>

        {modules.isLoading && <Skeleton className="mt-3 h-24 w-full" />}

        {modules.isError && (
          <p className="mt-3 text-sm text-destructive">{techCardErrorText(modules.error)}</p>
        )}

        {!modules.isLoading && !modules.isError && items.length === 0 && (
          <p className="mt-3 text-sm text-muted-foreground">
            В карту не вставлено ни одной системы. Строки защиты и питания, заведённые вручную,
            остаются на месте — система их не отменяет.
          </p>
        )}

        {items.length > 0 && (
          <div className="mt-3 grid gap-3 sm:grid-cols-2 lg:grid-cols-3">
            {items.map((row) => (
              <div key={row.module_id} className="rounded-md border border-border bg-background p-3">
                <div className="flex flex-wrap items-center gap-2">
                  <Badge variant="outline">{MODULE_KIND_SHORT[row.kind] ?? 'Система'}</Badge>
                  <span className="text-sm font-medium text-foreground">{row.name}</span>
                </div>
                {/* ₽/га системы ВНУТРИ ЭТОЙ КАРТЫ: считается по её строкам и
                    ценам её хозяйства, а не по библиотечной карточке системы —
                    поэтому сумма систем сходится с итогом карты. Подпись «в этой
                    карте» нужна именно потому, что в библиотеке у той же системы
                    цифра будет другой. Не приехало — цифры нет: прочерк тут
                    соврал бы, что стоимость посчитана и равна нулю. */}
                {row.cost_per_ha !== null && row.cost_per_ha !== undefined && (
                  <div className="mt-2 flex flex-wrap items-baseline gap-2">
                    <span className="text-lg font-semibold text-foreground">
                      {formatPerHa(row.cost_per_ha)}
                    </span>
                    <span className="text-xs text-muted-foreground">в этой карте</span>
                  </div>
                )}
                <div className="text-xs text-muted-foreground">
                  {formatTreatments(row.operations_count)}
                </div>
                {canWrite && (
                  <div className="mt-2 flex flex-wrap gap-2">
                    <Button
                      size="sm"
                      variant="outline"
                      onClick={() => setPicker({ kind: 'replace', current: row })}
                    >
                      Заменить
                    </Button>
                    {/* Систему поправили в библиотеке — карта подтягивает свежий
                        состав ОДНИМ действием. «Убрать и вставить заново» — два
                        запроса, и упавший второй оставил бы карту без защиты. */}
                    <Button
                      size="sm"
                      variant="ghost"
                      disabled={refresh.isPending}
                      onClick={() =>
                        refresh.mutate(
                          { oldModuleId: row.module_id, newModuleId: row.module_id },
                          {
                            onSuccess: () =>
                              toast.success(`Система «${row.name}» обновлена из библиотеки`),
                            onError: (err) => toast.error(techCardErrorText(err)),
                          },
                        )
                      }
                    >
                      Обновить
                    </Button>
                    <Button size="sm" variant="ghost" onClick={() => setRemoveFor(row)}>
                      Убрать
                    </Button>
                  </div>
                )}
              </div>
            ))}
          </div>
        )}
      </CardContent>

      {picker && canWrite && (
        <SystemPickerDialog
          cardId={cardId}
          seasonId={seasonId}
          cropId={cropId}
          mode={picker}
          inserted={items}
          onClose={() => setPicker(null)}
        />
      )}

      {removeFor && (
        <Dialog open onOpenChange={(o) => !o && setRemoveFor(null)}>
          <DialogContent>
            <DialogHeader>
              <DialogTitle>Убрать систему «{removeFor.name}» из карты?</DialogTitle>
              <DialogDescription>
                Из карты уйдут все обработки этой системы, себестоимость пересчитается. Сама
                система останется в библиотеке и в других картах.
              </DialogDescription>
            </DialogHeader>
            <DialogFooter>
              <Button
                variant="ghost"
                onClick={() => setRemoveFor(null)}
                disabled={remove.isPending}
              >
                Оставить
              </Button>
              <Button
                variant="destructive"
                disabled={remove.isPending}
                onClick={() =>
                  remove.mutate(
                    { moduleId: removeFor.module_id },
                    {
                      onSuccess: () => {
                        setRemoveFor(null);
                        toast.success('Система убрана — ₽/га пересчитан');
                      },
                    },
                  )
                }
              >
                Убрать систему
              </Button>
            </DialogFooter>
          </DialogContent>
        </Dialog>
      )}
    </Card>
  );
}
